/**
 * Customer history dialog — single-pane "who is this person?" view for admin.
 * Shows: lifetime stats, active bonos, all bookings, invoices, declared health.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Download, FileText, Calendar, Ticket, Heart, Euro, CheckCircle2,
  Send, ShieldCheck, ShieldAlert,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  cancelled: 'Cancelada',
  completed: 'Completada',
};

const STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-800',
};

export const CustomerHistoryDialog = ({ open, customerId, authHeader, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return;
    setLoading(true);
    axios
      .get(`${API}/admin/customer-cards/${customerId}/bookings`, {
        headers: { Authorization: authHeader },
      })
      .then((r) => setData(r.data))
      .catch(() => toast.error('No se pudo cargar el historial'))
      .finally(() => setLoading(false));
  }, [open, customerId, authHeader]);

  const downloadConsents = async (bookingId, label) => {
    try {
      const res = await axios.get(
        `${API}/admin/bookings/${bookingId}/consents-pdf`,
        { headers: { Authorization: authHeader }, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `consentimientos_${label}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Sin consentimientos guardados para esta reserva');
    }
  };

  const downloadInvoice = async (inv) => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${inv.id}/pdf`, {
        headers: { Authorization: authHeader },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al descargar la factura');
    }
  };

  /**
   * Issue a fresh signature link for a booking that was never signed (or
   * whose link expired). Reuses the resend endpoint and copies the URL.
   */
  const resendSignatureLink = async (b) => {
    try {
      const res = await axios.post(
        `${API}/admin/signature-requests/${b.id}/resend`,
        {},
        { headers: { Authorization: authHeader } },
      );
      if (res.data.email_sent) toast.success('Enlace reenviado por email');
      else {
        await navigator.clipboard.writeText(res.data.signature_url);
        toast.success('Enlace copiado (sin email del cliente)');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reenviar');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="customer-history-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {data?.customer
              ? `Historial · ${data.customer.full_name} (${data.customer.customer_number})`
              : 'Historial del cliente'}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center">
            <Loader2 className="animate-spin mx-auto text-halo-brown" size={32} />
          </div>
        )}

        {data && !loading && (
          <div className="space-y-5 mt-2">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <StatCard icon={Calendar} label="Reservas" value={data.stats.total_bookings} />
              <StatCard icon={CheckCircle2} label="Completadas" value={data.stats.completed_sessions} />
              <StatCard icon={Ticket} label="Bonos activos" value={data.stats.active_bonos} />
              <StatCard icon={Euro} label="Gasto total" value={`${data.stats.lifetime_spend.toFixed(2)}€`} />
            </div>

            {/* Health */}
            {data.health_declared.length > 0 && (
              <section className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h3 className="font-medium text-amber-900 flex items-center gap-2 mb-2">
                  <Heart size={16} /> Condiciones declaradas
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {data.health_declared.map((c) => (
                    <span
                      key={c}
                      className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-900"
                    >
                      {c.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Active bonos */}
            {data.active_bonos.length > 0 && (
              <section>
                <h3 className="font-medium text-halo-charcoal flex items-center gap-2 mb-2">
                  <Ticket size={16} /> Bonos activos
                </h3>
                <div className="space-y-2">
                  {data.active_bonos.map((b) => {
                    const used = b.sessions_used || 0;
                    const total = b.num_sessions || 0;
                    const remaining = total - used;
                    return (
                      <div
                        key={b.id}
                        className="bg-white border border-halo-taupe/15 rounded-lg p-3 flex justify-between items-center"
                      >
                        <div>
                          <div className="font-mono text-sm font-bold text-halo-brown">
                            {b.code}
                          </div>
                          <div className="text-xs text-halo-text">
                            {remaining}/{total} sesiones disponibles · válido hasta {b.valid_until || '-'}
                          </div>
                        </div>
                        <div className="text-right text-sm text-halo-charcoal font-medium">
                          {b.amount_paid?.toFixed?.(2) || b.price?.toFixed?.(2) || '-'}€
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Bookings */}
            <section>
              <h3 className="font-medium text-halo-charcoal flex items-center gap-2 mb-2">
                <Calendar size={16} /> Reservas ({data.bookings.length})
              </h3>
              {data.bookings.length === 0 && (
                <p className="text-sm text-halo-text italic">Aún no tiene reservas.</p>
              )}
              <div className="space-y-1.5">
                {data.bookings.map((b) => {
                  const isSigned = b.signature_status === 'signed' || (b.consents && b.consents.accepted_data_consent);
                  return (
                    <div
                      key={b.id}
                      className="bg-white border border-halo-taupe/15 rounded-lg p-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-sm font-mono">{b.date} {b.time}</span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[b.status] || 'bg-gray-100'}`}
                          >
                            {STATUS_LABEL[b.status] || b.status}
                          </span>
                          <span className="text-xs text-halo-text truncate">
                            {b.service_name} · {b.room === 'sala_grande' ? 'Sala 2' : 'Sala 1'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {/* Signature badge */}
                          {isSigned ? (
                            <span
                              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800"
                              title={b.signature_at ? `Firmado el ${new Date(b.signature_at).toLocaleString('es-ES')}` : ''}
                            >
                              <ShieldCheck size={12} /> Firmado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                              <ShieldAlert size={12} /> Sin firmar
                            </span>
                          )}
                          {isSigned && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadConsents(b.id, b.date)}
                              data-testid={`history-consents-${b.id}`}
                            >
                              <FileText size={12} className="mr-1" /> Firmas
                            </Button>
                          )}
                          {!isSigned && b.signature_token && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => resendSignatureLink(b)}
                              data-testid={`history-resend-sig-${b.id}`}
                            >
                              <Send size={12} className="mr-1" /> Enviar firma
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Invoices */}
            {data.invoices.length > 0 && (
              <section>
                <h3 className="font-medium text-halo-charcoal flex items-center gap-2 mb-2">
                  <FileText size={16} /> Facturas ({data.invoices.length})
                </h3>
                <div className="space-y-1.5">
                  {data.invoices.map((inv) => (
                    <div
                      key={inv.id}
                      className="bg-white border border-halo-taupe/15 rounded-lg p-3 flex justify-between items-center"
                    >
                      <div>
                        <div className="font-mono text-sm">{inv.number}</div>
                        <div className="text-xs text-halo-text">
                          {(inv.issued_at || '').slice(0, 10)} · {inv.service_name}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">{inv.amount?.toFixed(2)}€</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadInvoice(inv)}
                        >
                          <Download size={12} className="mr-1" /> PDF
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-halo-cream rounded-xl p-3 text-center">
    <Icon className="mx-auto mb-1 text-halo-brown" size={18} />
    <div className="text-lg font-bold text-halo-charcoal">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-halo-text">{label}</div>
  </div>
);

export default CustomerHistoryDialog;
